/**
 * FX Stale Alert Banner
 * Displays warning when FX rates haven't been updated for >6 hours
 * Polls every 5 minutes to check cache status
 */
import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import toast from 'react-hot-toast';

const FXStaleAlert = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    checkStatus();
    // Poll every 5 minutes
    const interval = setInterval(checkStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const checkStatus = async () => {
    try {
      const response = await api.get('/fx/cache/status');
      if (response.success) {
        setStatus(response.data);
      }
    } catch (error) {
      console.error('Failed to check FX cache status:', error);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await api.post('/admin/fx/refresh');
      
      if (response.success) {
        toast.success('FX rates refreshed successfully');
        await checkStatus();
      } else {
        toast.error(response.message || 'Failed to refresh FX rates');
      }
    } catch (error) {
      console.error('Failed to refresh FX rates:', error);
      toast.error(error.message || 'Failed to refresh FX rates');
    } finally {
      setRefreshing(false);
    }
  };

  // Don't show banner if rates are fresh or status not loaded
  if (!status || !status.isStale) {
    return null;
  }

  return (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-r-lg shadow-sm">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <i className="fas fa-exclamation-triangle text-yellow-400 text-xl"></i>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-semibold text-yellow-800">
            ⚠️ FX Rates Are Stale
          </h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p>
              Exchange rates haven't been updated for{' '}
              <strong>{status.ageHours?.toFixed(1)} hours</strong>.
              Pricing may be inaccurate.
            </p>
            <p className="mt-1 text-xs">
              Last update: {new Date(status.lastFetch).toLocaleString()}
            </p>
          </div>
          <div className="mt-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {refreshing ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Refreshing...
                </>
              ) : (
                <>
                  <i className="fas fa-sync-alt mr-2"></i>
                  Refresh Now
                </>
              )}
            </button>
          </div>
        </div>
        <div className="ml-auto pl-3">
          <button
            onClick={checkStatus}
            className="text-yellow-600 hover:text-yellow-800 transition-colors"
            title="Recheck status"
          >
            <i className="fas fa-redo text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FXStaleAlert;
